/* eslint-disable no-param-reassign */
import React, { useContext, useState, useEffect } from 'react';
import { IconButton } from '@mui/material';
import { colors } from '@roku-web-ui/rds-theme';
import { useNavigate } from '@tanstack/react-router';

import { EventsContext } from 'context/events';
import { eventsReducer } from 'context/reducers/events';
import { IconDelete } from 'components/icons';
import { Tooltip } from 'components/common/Tooltip';
import { DeleteStatesPayload, DeleteStatesRespRxMsg } from 'types/aspen/socketMessages';

interface DeleteProps {
  eventId: string;
  deviceId?: string;
  onDeleteStart?: () => void;
}

export const Delete = ({ eventId, deviceId, onDeleteStart }: DeleteProps) => {
  const { sendRequest, eventsStore, setModalOpen } = useContext(EventsContext);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timeout = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timeout);
    }
  }, [error]);

  const handleDelete = async () => {
    if (deleting) return;

    try {
      setDeleting(true);
      setError(null); // Clear any previous error
      onDeleteStart?.();

      // Use the RPC system to delete the event
      const payload: DeleteStatesPayload = [{ leafId: deviceId, stateIds: [eventId] }];

      await sendRequest<DeleteStatesRespRxMsg['payload'], DeleteStatesPayload>(
        'delete_states',
        'delete_states_resp',
        payload,
        { logEventType: 'iot.web.event.delete' },
      );

      // Remove the event from local storage after successful deletion
      const currentState = eventsStore.read();
      const newState = eventsReducer(currentState, {
        type: 'REMOVE_EVENT',
        payload: { eventId },
      });
      eventsStore.write(newState);

      // Close modal and navigate back to events list
      setModalOpen(false);
      navigate({
        to: '/events',
        replace: true,
      });
    } catch (err) {
      // Set error message for tooltip display
      setError(err instanceof Error ? err.message : 'Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  const tooltipTitle = (() => {
    if (error) return `Delete failed: ${error}`;
    if (deleting) return 'Deleting...';
    return 'Delete event';
  })();

  const getIconColor = () => {
    if (error) return colors.red[500];
    if (deleting) return colors.grey[600];
    return colors.grey[400];
  };

  return (
    <Tooltip title={tooltipTitle} placement="top">
      <div>
        <IconButton
          aria-label="delete event"
          data-testid="button-delete"
          disabled={deleting || !!error}
          onClick={handleDelete}
          sx={{
            '&.MuiIconButton-root': {
              padding: '0', // Override RDS padding
            },
            '&:disabled': {
              opacity: 0.5,
              pointerEvents: deleting || error ? 'auto' : 'none', // Allow tooltip during delete/error
            },
            '&:hover': {
              boxShadow: 'none',
            },
          }}
        >
          <IconDelete sx={{ color: getIconColor() }} />
        </IconButton>
      </div>
    </Tooltip>
  );
};
